/**
 * Persist Photo Studio reels in Firestore + Storage so they survive refresh.
 */

import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type Timestamp,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { STORAGE_PATHS } from '@canvas/firebase-config/config';
import { getClientAuth, getClientDB, getClientStorage } from '@canvas/firebase-config/client';

export type ReelMode = 'ai' | 'local';

export interface StudioReel {
  id: string;
  projectId: string;
  videoUrl: string;
  storagePath: string;
  mode: ReelMode;
  platformId?: string;
  motionStyleId?: string;
  width: number;
  height: number;
  durationSec: number;
  createdAt?: Timestamp | null;
}

function reelsCol(uid: string, projectId: string) {
  return collection(getClientDB(), 'users', uid, 'projects', projectId, 'reels');
}

export async function resolveReelPlaybackUrl(storagePath: string): Promise<string> {
  return getDownloadURL(ref(getClientStorage(), storagePath));
}

/** List recent reels for a project (newest first). */
export async function listProjectReels(
  projectId: string,
  max = 12
): Promise<StudioReel[]> {
  const uid = getClientAuth().currentUser?.uid;
  if (!uid || !projectId) return [];

  const q = query(reelsCol(uid, projectId), orderBy('createdAt', 'desc'), limit(max));
  const snap = await getDocs(q);
  const out: StudioReel[] = [];

  for (const d of snap.docs) {
    const data = d.data();
    const storagePath = String(data.storagePath || '');
    let videoUrl = String(data.videoUrl || '');
    if (storagePath) {
      try {
        videoUrl = await resolveReelPlaybackUrl(storagePath);
      } catch {
        /* keep stored url if refresh fails */
      }
    }
    if (!videoUrl) continue;
    out.push({
      id: d.id,
      projectId,
      videoUrl,
      storagePath,
      mode: data.mode === 'ai' ? 'ai' : 'local',
      platformId: data.platformId,
      motionStyleId: data.motionStyleId,
      width: Number(data.width) || 1080,
      height: Number(data.height) || 1920,
      durationSec: Number(data.durationSec) || 5,
      createdAt: data.createdAt ?? null,
    });
  }
  return out;
}

/** Upload a local/Ken Burns blob and save metadata. */
export async function persistLocalReel(options: {
  projectId: string;
  blob: Blob;
  platformId?: string;
  motionStyleId?: string;
  width: number;
  height: number;
  durationSec: number;
}): Promise<StudioReel> {
  const uid = getClientAuth().currentUser?.uid;
  if (!uid) throw new Error('Sign in to save reels');

  const reelId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const ext = options.blob.type.includes('mp4') ? 'mp4' : 'webm';
  const storagePath = `${STORAGE_PATHS.projectReels(uid, options.projectId)}/${reelId}.${ext}`;
  const storageRef = ref(getClientStorage(), storagePath);

  await uploadBytes(storageRef, options.blob, {
    contentType: options.blob.type || (ext === 'mp4' ? 'video/mp4' : 'video/webm'),
    customMetadata: {
      projectId: options.projectId,
      mode: 'local',
    },
  });

  const videoUrl = await getDownloadURL(storageRef);
  const meta = {
    projectId: options.projectId,
    videoUrl,
    storagePath,
    mode: 'local' as const,
    platformId: options.platformId || null,
    motionStyleId: options.motionStyleId || null,
    width: options.width,
    height: options.height,
    durationSec: options.durationSec,
    createdAt: serverTimestamp(),
  };

  await setDoc(doc(reelsCol(uid, options.projectId), reelId), meta);

  return {
    id: reelId,
    projectId: options.projectId,
    videoUrl,
    storagePath,
    mode: 'local',
    platformId: options.platformId,
    motionStyleId: options.motionStyleId,
    width: options.width,
    height: options.height,
    durationSec: options.durationSec,
  };
}

/** Save metadata for an AI reel already uploaded by Cloud Functions. */
export async function persistAiReelMeta(options: {
  projectId: string;
  reelId: string;
  videoUrl: string;
  storagePath: string;
  platformId?: string;
  motionStyleId?: string;
  width: number;
  height: number;
  durationSec: number;
}): Promise<StudioReel> {
  const uid = getClientAuth().currentUser?.uid;
  if (!uid) throw new Error('Sign in to save reels');

  // Prefer auth download URL when path is under projects/
  let videoUrl = options.videoUrl;
  if (options.storagePath.startsWith('projects/')) {
    try {
      videoUrl = await resolveReelPlaybackUrl(options.storagePath);
    } catch {
      /* keep signed url */
    }
  }

  await setDoc(doc(reelsCol(uid, options.projectId), options.reelId), {
    projectId: options.projectId,
    videoUrl,
    storagePath: options.storagePath,
    mode: 'ai',
    platformId: options.platformId || null,
    motionStyleId: options.motionStyleId || 'ai-breathe',
    width: options.width,
    height: options.height,
    durationSec: options.durationSec,
    createdAt: serverTimestamp(),
  });

  return {
    id: options.reelId,
    projectId: options.projectId,
    videoUrl,
    storagePath: options.storagePath,
    mode: 'ai',
    platformId: options.platformId,
    motionStyleId: options.motionStyleId || 'ai-breathe',
    width: options.width,
    height: options.height,
    durationSec: options.durationSec,
  };
}
